import { useAtom, useAtomValue } from 'jotai';
import { Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDecision, useFeature } from '@optimizely/react-sdk';

import { opalAuthAtom, opalOverlayOpenAtom } from '@frontend/atoms';
import FullScreenLoader from '@frontend/components/FullScreenLoader';
import chatConfig from '@frontend/config/chat';
import { EVENT_NAME, FeatureFlags, OVERLAY_PAGE_SEGMENTS } from '@frontend/constants';
import { useOpalUser } from '@frontend/hooks/useOpalUser';

import { LazyAIChatComponent } from './LazyAiChatComponent';
import { overlayPanelStyles } from './OpalChatOverlay.css';

export function OpalChatOverlay() {
  const [isOpen, setIsOpen] = useAtom(opalOverlayOpenAtom);
  const { opalUser } = useOpalUser();
  const opalAuth = useAtomValue(opalAuthAtom);
  const { pathname } = useLocation();
  const [decision] = useDecision(FeatureFlags.OPAL_CHAT_OVERLAY);
  const [chatFromFF, primaryAgentConfig] = useFeature('opal_app_features');
  const initialPrompt =
    (chatFromFF ? primaryAgentConfig.chat_initial_prompt?.initialPrompt : null) ||
    chatConfig.initialPrompt;

  const segments = pathname.split('/');
  const isOnOverlayPage = OVERLAY_PAGE_SEGMENTS.some(s => segments.includes(s));

  useEffect(() => {
    const onClosed = () => setIsOpen(false);
    window.addEventListener(EVENT_NAME.OPAL_CHATBOX_CLOSED, onClosed);
    return () => window.removeEventListener(EVENT_NAME.OPAL_CHATBOX_CLOSED, onClosed);
  }, [setIsOpen]);

  useEffect(() => {
    if (!isOnOverlayPage) {
      setIsOpen(false);
    }
  }, [isOnOverlayPage, setIsOpen]);

  if (!decision.enabled || !isOpen || !opalAuth || !opalUser || segments.includes('chat'))
    return null;

  return (
    <div className={overlayPanelStyles}>
      <Suspense fallback={<FullScreenLoader />}>
        <LazyAIChatComponent
          currentUser={{
            instanceId: opalUser.instance.instance_id,
            avatarUrl: '',
            name: opalUser.firstname + ' ' + opalUser.lastname,
            operations: opalUser.instance.operations.filter(op => op.allowed).map(op => op.name),
          }}
          storeKey="overlay"
          productScope="OPAL"
          showHeaderIcons
          sidebarAlwaysVisible={false}
          initialDockState="docked"
          allowPopout={false}
          initialPrompt={initialPrompt}
          opalAuth={opalAuth}
        />
      </Suspense>
    </div>
  );
}
